// Generated from Swagger on 2025-08-27T14:46:24.215Z
import {
  useSuspenseQuery,
  useQuery,
  useMutation,
  useQueryClient,
  UseSuspenseQueryOptions,
  UseQueryOptions,
  UseMutationOptions,
} from "@tanstack/react-query";
import { useApiClient } from "../client";
import { creationEntitiesKeys } from "../creation-entities-keys";
import { formatSearchQuery } from "../../utils/format-search-query";
import {
  CustomerUserListPaginatedResponseDtoWithInvitationCount,
  CustomerUserListPaginatedResponseDto,
  CustomerInvitationListPaginatedResponseDto,
  CreateCustomerInvitationRequestDto,
  CustomerInvitationResponseDto,
} from "../interfaces/users.interface";

export interface UseUsersCustomerFindInitialParams {
  /**
   * The maximum amount of retrieved items per page
   * @constraints minimum: 1, maximum: 20, default: 20, example: 20
   */
  limit?: number;
  /**
   * The page number to retrieve items from.
   * @constraints minimum: 1, default: 1, example: 1
   */
  page?: number;
  /**
   * The text to search for
   * @constraints minLength: 4, example: Jobdescription
   */
  textSearch?: string;
}

export interface UseUsersCustomerFindParams {
  /**
   * The text to search for
   * @constraints minLength: 4, example: Jobdescription
   */
  textSearch?: string;
  /**
   * The maximum amount of retrieved items per page
   * @constraints minimum: 1, maximum: 20, default: 20, example: 20
   */
  limit?: number;
  /**
   * The page number to retrieve items from.
   * @constraints minimum: 1, default: 1, example: 1
   */
  page?: number;
}

export interface UseUsersCustomerFindInvitationsParams {
  /**
   * The maximum amount of retrieved items per page
   * @constraints minimum: 1, maximum: 20, default: 20, example: 20
   */
  limit?: number;
  /**
   * The page number to retrieve items from.
   * @constraints minimum: 1, default: 1, example: 1
   */
  page?: number;
}

// Query keys for users-related queries
export const usersKeys = {
  all: ["users"] as const,
  initial: (params?: any) => [...usersKeys.all, "initial", params] as const,
  profile: () => [...usersKeys.all, "profile"] as const,
  detail_archive_mutation: (params?: any) =>
    [...usersKeys.all, "detail_archive_mutation", params] as const,
  detail_unarchive_mutation: (params?: any) =>
    [...usersKeys.all, "detail_unarchive_mutation", params] as const,
  creation_entities: () => [...usersKeys.all, "creation_entities"] as const,
  invitations: (params?: any) =>
    [...usersKeys.all, "invitations", params] as const,
  invitations_mutation: () =>
    [...usersKeys.all, "invitations_mutation"] as const,
  invitations_detail_mutation: (params?: any) =>
    [...usersKeys.all, "invitations_detail_mutation", params] as const,
};

/**
 * Find customer staff users with pagination and details for initial request
 */
export function useUsersCustomerFindInitial(
  params?: UseUsersCustomerFindInitialParams,
  options?: Omit<
    UseSuspenseQueryOptions<CustomerUserListPaginatedResponseDtoWithInvitationCount>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: [
      ...usersKeys.initial({ ...params }),
      ...creationEntitiesKeys.list(),
    ],
    queryFn: () => {
      const queryString = formatSearchQuery(params);
      return apiRequest<CustomerUserListPaginatedResponseDtoWithInvitationCount>(
        `/users/initial${queryString}`,
      );
    },
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Non-suspense version of useUsersCustomerFindInitial
 */
export function useUsersCustomerFindInitialQuery(
  params?: UseUsersCustomerFindInitialParams,
  options?: Omit<
    UseQueryOptions<CustomerUserListPaginatedResponseDtoWithInvitationCount>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: [
      ...usersKeys.initial({ ...params }),
      ...creationEntitiesKeys.list(),
    ],
    queryFn: () => {
      const queryString = formatSearchQuery(params);
      return apiRequest<CustomerUserListPaginatedResponseDtoWithInvitationCount>(
        `/users/initial${queryString}`,
      );
    },
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Find customer staff users with pagination
 */
export function useUsersCustomerFind(
  params?: UseUsersCustomerFindParams,
  options?: Omit<
    UseSuspenseQueryOptions<CustomerUserListPaginatedResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: [...usersKeys.profile(), ...creationEntitiesKeys.list(), params],
    queryFn: () => {
      const queryString = formatSearchQuery(params);
      return apiRequest<CustomerUserListPaginatedResponseDto>(
        `/users${queryString}`,
      );
    },
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Non-suspense version of useUsersCustomerFind
 */
export function useUsersCustomerFindQuery(
  params?: UseUsersCustomerFindParams,
  options?: Omit<
    UseQueryOptions<CustomerUserListPaginatedResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: [...usersKeys.profile(), ...creationEntitiesKeys.list(), params],
    queryFn: () => {
      const queryString = formatSearchQuery(params);
      return apiRequest<CustomerUserListPaginatedResponseDto>(
        `/users${queryString}`,
      );
    },
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Archive customer users
 */
export function useUsersCustomerarchiveOne(
  options?: Omit<UseMutationOptions<any, Error, { id: string }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id }: { id: string }) =>
      apiRequest<any>(`/users/${id}/archive`, "PATCH"),
    onSuccess: (data, variables, context) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: usersKeys.all });
      // Invalidate all queries that contain creation entities keys
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey.includes("creation-entities");
        },
      });
      // Call user-provided onSuccess if it exists
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Unarchive customer users
 */
export function useUsersCustomerunArchiveOne(
  options?: Omit<UseMutationOptions<any, Error, { id: string }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id }: { id: string }) =>
      apiRequest<any>(`/users/${id}/unarchive`, "PATCH"),
    onSuccess: (data, variables, context) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: usersKeys.all });
      // Invalidate all queries that contain creation entities keys
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey.includes("creation-entities");
        },
      });
      // Call user-provided onSuccess if it exists
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Get customer staff users invitations creation entities
 */
export function useUsersCustomerinvitationsCreationEntities(
  options?: Omit<UseSuspenseQueryOptions<any>, "queryKey" | "queryFn">,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: creationEntitiesKeys.list(),
    queryFn: () => apiRequest<any>(`/users/invitations/creation-entities`),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Non-suspense version of useUsersCustomerinvitationsCreationEntities
 */
export function useUsersCustomerinvitationsCreationEntitiesQuery(
  options?: Omit<UseQueryOptions<any>, "queryKey" | "queryFn">,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: creationEntitiesKeys.list(),
    queryFn: () => apiRequest<any>(`/users/invitations/creation-entities`),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Find customer staff users invitations with pagination
 */
export function useUsersCustomerFindInvitations(
  params?: UseUsersCustomerFindInvitationsParams,
  options?: Omit<
    UseSuspenseQueryOptions<CustomerInvitationListPaginatedResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: [
      ...usersKeys.invitations({ ...params }),
      ...creationEntitiesKeys.list(),
    ],
    queryFn: () => {
      const queryString = formatSearchQuery(params);
      return apiRequest<CustomerInvitationListPaginatedResponseDto>(
        `/users/invitations${queryString}`,
      );
    },
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Non-suspense version of useUsersCustomerFindInvitations
 */
export function useUsersCustomerFindInvitationsQuery(
  params?: UseUsersCustomerFindInvitationsParams,
  options?: Omit<
    UseQueryOptions<CustomerInvitationListPaginatedResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: [
      ...usersKeys.invitations({ ...params }),
      ...creationEntitiesKeys.list(),
    ],
    queryFn: () => {
      const queryString = formatSearchQuery(params);
      return apiRequest<CustomerInvitationListPaginatedResponseDto>(
        `/users/invitations${queryString}`,
      );
    },
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Invite customer staff user
 */
export function useUsersCustomerCreateInvitation(
  options?: Omit<
    UseMutationOptions<
      CustomerInvitationResponseDto,
      Error,
      CreateCustomerInvitationRequestDto
    >,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (data: CreateCustomerInvitationRequestDto) =>
      apiRequest<CustomerInvitationResponseDto>(
        `/users/invitations`,
        "POST",
        data,
      ),
    onSuccess: (data, variables, context) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: usersKeys.all });
      // Invalidate all queries that contain creation entities keys
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey.includes("creation-entities");
        },
      });
      // Call user-provided onSuccess if it exists
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Abort customer staff user invitation
 */
export function useUsersCustomerDeleteInvitation(
  options?: Omit<UseMutationOptions<any, Error, { id: string }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id }: { id: string }) =>
      apiRequest<any>(`/users/invitations/${id}`, "DELETE"),
    onSuccess: (data, variables, context) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: usersKeys.all });
      // Invalidate all queries that contain creation entities keys
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey.includes("creation-entities");
        },
      });
      // Call user-provided onSuccess if it exists
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Resend customer staff user invitation
 */
export function useUsersCustomerResendInvitation(
  options?: Omit<UseMutationOptions<any, Error, { id: string }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id }: { id: string }) =>
      apiRequest<any>(`/users/invitations/${id}/resend`, "POST"),
    onSuccess: (data, variables, context) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: usersKeys.all });
      // Call user-provided onSuccess if it exists
      options?.onSuccess?.(data, variables, context);
    },
  });
}
